import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSignIn } from '@clerk/clerk-react';
import { Button, Form, TextInput, Stack, InlineNotification } from '@carbon/react';
import { useAuth } from '@/contexts/AuthContext';
import { getClerkErrorMessage } from '@/lib/clerkErrors';
import { AuthPageLayout, ClerkMissingNotice } from '@/components/auth/AuthShell';

const ForgotPasswordContent = () => {
  const navigate = useNavigate();
  const { isLoaded, signIn } = useSignIn();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isLoaded || !signIn) return;

    setSubmitting(true);
    setError('');

    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email.trim(),
      });

      navigate('/reset-password', {
        replace: true,
        state: { email: email.trim() },
      });
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthPageLayout
      title="Reset your password"
      description="Enter your account email and we will send you a reset code."
      testId="forgot-password-page"
    >
      {error && (
        <InlineNotification
          kind="error"
          title="Unable to continue"
          subtitle={error}
          hideCloseButton
          className="auth-form-error"
        />
      )}
      <Form className="auth-form" onSubmit={handleSubmit}>
        <Stack gap={6}>
          <TextInput
            id="forgot-email"
            labelText="Email"
            type="email"
            placeholder="you@sensestack.com.au"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
          <Button type="submit" disabled={!isLoaded || submitting || !email.trim()}>
            {submitting ? 'Sending code…' : 'Send reset code'}
          </Button>
          <p className="auth-form-switch">
            Remember your password?{' '}
            <Link to="/login" className="auth-form-link">
              Back to sign in
            </Link>
          </p>
        </Stack>
      </Form>
    </AuthPageLayout>
  );
};

const ForgotPasswordPage = () => {
  const { clerkMissing } = useAuth();
  if (clerkMissing) return <ClerkMissingNotice />;
  return <ForgotPasswordContent />;
};

export default ForgotPasswordPage;
